import { NOT_AVAILABLE } from 'consts/consts';
import { type IntlShape } from 'react-intl';
import {
  CalibrationItem,
  CalibrationItemWithDisplay,
  TeamDashboardAMDraftRating,
  TeamDashboardAMRecommendForPromotion,
  TeamDashboardAMRecommendForSalaryIncrease,
} from 'types';
import { WHITELISTED_FIELDS_FOR_SENSITIVE_DATA_OMISSION } from 'utils/models/Person';
import { SENSITIVE_DATA_OMITTED } from 'utils/models/SurveyResponse';
import { formatDateWithUnicode } from 'utils/util/time';
import {
  isRawValueFollowedByOutputValueArrayOfTwo,
  stripHtml,
} from 'utils/util/util';
import { decoupleValAndCalibrationInfo } from '../Calibration/utils';
import {
  getCalibrationStatusDescriptionFromValue,
  getReportAcknowledgementStatusDescriptionFromValue,
  getReportReleaseStatusDescriptionFromValue,
  RATING_IGNORE_DUE_TO_NO_MANAGER,
  RATING_NOT_ALLOWED,
  RATING_NOT_ELIGIBLE_TO_MANAGER_REVIEW,
  RATING_NOT_PARTICIPATING,
  RATING_NOT_YET_COMPLETE,
} from '../helpers';

const convertIneligibleRatingToText = (rating, formatMessage) => {
  if (rating === RATING_NOT_PARTICIPATING) {
    return formatMessage({
      id: 'app.views.widgets.dashboards.team_dashboard.not participating',
      defaultMessage: 'Not participating',
    });
  } else if (rating === RATING_NOT_YET_COMPLETE) {
    return formatMessage({
      id: 'app.views.widgets.dashboards.team_dashboard.rating_incomplete',
      defaultMessage: 'Rating incomplete',
    });
  } else if (rating === RATING_IGNORE_DUE_TO_NO_MANAGER) {
    return formatMessage({
      id: 'app.views.widgets.dashboards.team_dashboard.no_manager',
      defaultMessage: 'No manager',
    });
  } else if (rating === RATING_NOT_ALLOWED) {
    return formatMessage({
      id: 'app.views.widgets.dashboards.team_dashboard.no_rating_allowed',
      defaultMessage: 'No rating allowed',
    });
  } else if (rating === RATING_NOT_ELIGIBLE_TO_MANAGER_REVIEW) {
    return formatMessage({
      id: 'app.views.widgets.dashboards.team_dashboard.not_eligible_to_receive_rating',
      defaultMessage: 'Ineligible to receive rating',
    });
  }
  return rating;
};

const convertAdditionalMangerDraftRatingToText = (
  value: TeamDashboardAMDraftRating[],
  formatMessage: IntlShape['formatMessage']
) => {
  return value
    .map(
      ({ manager, draft_rating, draft_rating_comments }) =>
        `${manager.full_name} (Rating: ${draft_rating ?? '-'}, ${
          stripHtml(draft_rating_comments, true) ??
          formatMessage({
            id: 'app.views.widgets.dashboards.team_dashboard.no_comments_provided',
            defaultMessage: 'No comments provided',
          })
        })`
    )
    .join(', ');
};

const convertAdditionalMangerSalaryIncreaseToText = (
  value: TeamDashboardAMRecommendForSalaryIncrease[],
  formatMessage: IntlShape['formatMessage']
) => {
  return value
    .map(
      ({ manager, recommend_for_salary_increase, salary_increase_comments }) =>
        `${manager.full_name} (Recommended: ${recommend_for_salary_increase}, ${
          salary_increase_comments ??
          formatMessage({
            id: 'app.views.widgets.dashboards.team_dashboard.no_comments_provided',
            defaultMessage: 'No comments provided',
          })
        })`
    )
    .join(', ');
};

const convertAdditionalMangerPromotionReccomendationToText = (
  value: TeamDashboardAMRecommendForPromotion[],
  formatMessage: IntlShape['formatMessage']
) => {
  return value
    .map(
      ({ manager, recommend_for_promotion, promotion_comments }) =>
        `${manager.full_name} (Recommended: ${recommend_for_promotion}, ${
          promotion_comments ??
          formatMessage({
            id: 'app.views.widgets.dashboards.team_dashboard.no_comments_provided',
            defaultMessage: 'No comments provided',
          })
        })`
    )
    .join(', ');
};

export const formatForCSV = (
  array,
  personIdToMetadataMap,
  headers,
  surveyResponseIdToCalibrationItems:
    | Record<number, CalibrationItem[]>
    | undefined,
  allowedColumns: Set<string> | undefined,
  formatMessage
) => {
  const headersDict = headers.reduce((acc, curr) => {
    acc[curr.key] = curr;
    return acc;
  }, {});

  return array.map((p) => {
    let person = p;

    // if there is a personIdToMetadataMap, we need to merge it
    // into the person object we're looking at
    if (
      personIdToMetadataMap &&
      person.id in personIdToMetadataMap &&
      personIdToMetadataMap[person.id]
    ) {
      person = { ...person, ...personIdToMetadataMap[person.id] };
    }

    // need to convert to prevent issues when folks use "'s
    return Object.keys(person).reduce((result, key) => {
      if (allowedColumns && !allowedColumns.has(key)) {
        return result;
      }

      const decoupledValAndCal = decoupleValAndCalibrationInfo(person[key]);
      let value = decoupledValAndCal.val;
      const calibrationInfo = decoupledValAndCal.calibrationInfo;

      const header = headersDict[key];

      if (header && header.formatToCsvValue) {
        value = header.formatToCsvValue(value);
      }
      // for final_rating and final_rating_comments, if they were modified
      // in this session we want to include those in the export
      else if (key == 'final_rating') {
        value = convertIneligibleRatingToText(value, formatMessage);
      } else if (key == 'draft_rating') {
        value = convertIneligibleRatingToText(value, formatMessage);
      } else if (
        key == 'draft_rating_comments' ||
        key == 'final_rating_comments'
      ) {
        value = stripHtml(value, true);
      } else if (key == 'draft_rating_additional_managers') {
        value = value
          ? convertAdditionalMangerDraftRatingToText(value, formatMessage)
          : '';
      } else if (key == 'recommend_for_salary_increase_additional_managers') {
        value = value
          ? convertAdditionalMangerSalaryIncreaseToText(value, formatMessage)
          : '';
      } else if (key == 'recommend_for_promotion_additional_managers') {
        value = value
          ? convertAdditionalMangerPromotionReccomendationToText(
              value,
              formatMessage
            )
          : '';
      } else if (key == 'original_calibration_flags') {
        if (Array.isArray(value)) {
          // if just 1 item, output it, otherwise output as a list
          if (value.length === 1) {
            value = value[0];
          } else {
            // output as list with numbers at beginning and new lines
            // between each item
            value = value.map((v, index) => `${index + 1}. ${v}`).join('\n\n');
          }
        } else {
          console.error('Unexpected value type for original_calibration_flags');
        }
      } else if (key == 'calibration_status') {
        value = getCalibrationStatusDescriptionFromValue(value, formatMessage);
      } else if (key == 'report_release_status') {
        value = getReportReleaseStatusDescriptionFromValue(
          value,
          formatMessage
        );
      } else if (key == 'report_acknowledgement_status') {
        value = getReportAcknowledgementStatusDescriptionFromValue(
          value,
          formatMessage
        );
      } else if (key == 'incoming_peers' || key == 'outgoing_peers') {
        value = (value ?? []).map((p) => p.full_name).join(', ');
      }

      // For custom responses, e.g. upward feedback:
      // if value is object, convert to human-readable string
      if (value && typeof value === 'object') {
        // if an array of 2, first value is raw value and second is
        // friendly value for display in table, so take first value
        if (isRawValueFollowedByOutputValueArrayOfTwo(value)) {
          // handle multiple choice, and dropdown questions -- don't show
          // unfriendly uuid, instead show user-friendly display value
          // it would best to use the question type here, but it's not
          // passed down to this point
          value = value[1];
        } else {
          // if this is an array of objects, could be a people editor response
          // (which is a list of objects)
          if (
            Array.isArray(value) &&
            value.every((v) => typeof v === 'object')
          ) {
            value = value
              .map((v) => {
                if (v.email && v.full_name) {
                  return `${v.full_name} (${v.email})`;
                } else if (v.full_name) {
                  return v.full_name;
                } else if (v.name) {
                  return v.name;
                } else {
                  return v;
                }
              })
              .join(', ');
          } else {
            const displayValueForCSV = (v) => {
              if (isRawValueFollowedByOutputValueArrayOfTwo(v)) {
                if (v[0] && v[0].startsWith && v[0].startsWith('id_')) {
                  return v[1];
                } else {
                  return `(${v[0]}) ${v[1]}`;
                }
              } else {
                return v;
              }
            };

            // this is a dict; key-values are name: <value which could be array like above>
            value = Object.keys(value)
              .map((k) => (k ? `${k}: ${displayValueForCSV(value[k])}` : ''))
              .join('\n\n');
          }
        }
      }

      if (
        !!person[SENSITIVE_DATA_OMITTED] &&
        !WHITELISTED_FIELDS_FOR_SENSITIVE_DATA_OMISSION.includes(key)
      ) {
        value = NOT_AVAILABLE;
      }

      // Calibration info for custom questions
      if (calibrationInfo && calibrationInfo.calibrationItems) {
        // handle calibration info if present, on the same field.
        value += `\n\n${formatCalibrationInfoForCSV(
          calibrationInfo.calibrationItems
        )}`;
      }

      const survey_response_id = person?.['survey_response_id'];

      // Calibration info for other fields like recommend_for_promotion
      if (
        FIELDS_WITH_CALIBRATION.has(key) &&
        surveyResponseIdToCalibrationItems &&
        !!surveyResponseIdToCalibrationItems?.[survey_response_id] &&
        !!surveyResponseIdToCalibrationItems?.[survey_response_id].length
      ) {
        const calibrationItemsForField = surveyResponseIdToCalibrationItems[
          survey_response_id
        ].filter((ci) => ci.key === key);
        if (calibrationItemsForField.length) {
          value += `\n\n${formatCalibrationInfoForCSV(
            calibrationItemsForField.map((ci) => ({
              ...ci,
              displayValue: ci.value != null ? ci.value.toString() : '',
            }))
          )}`;
        }
      }

      result[key] =
        typeof value === 'string' ? value.replace(/"/g, '""') : value;
      return result;
    }, {});
  });
};

const FIELDS_WITH_CALIBRATION = new Set<string>(['recommend_for_promotion']);

const formatCalibrationInfoForCSV = (
  calibrationItems: CalibrationItemWithDisplay[]
): string => {
  return calibrationItems
    .map(
      (ci) =>
        `(Calibration - ${formatDateWithUnicode(
          new Date(ci.created_at),
          'yyyy-MM-dd'
        )}) ${ci.author.full_name}: ${ci.displayValue} ${stripHtml(ci.comment, true) ?? ''}`
    )
    .join('\n\n');
};
